"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import SubjectForm from "@/components/templates/SubjectForm";
import {
  buildFormInitialData,
  buildSubjectFormSchema,
  normalizeSubjectFormData,
} from "@/components/templates/SubjectForm/form-data";
import { SubjectFormData } from "@/components/templates/SubjectForm/types";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useCreateSubject from "@/hooks/requests/subject/use-create-subject";
import useGetSubjectById from "@/hooks/requests/subject/use-get-subject-by-id";
import useUpdateSubject from "@/hooks/requests/subject/use-update-subject";
import useMutate from "@/hooks/use-mutate";
import { SubjectFormMessages } from "@/messages/subject/subject-form.messages";
import { BreadCrumb } from "@/types/page.types";

type Props = {
  messages: SubjectFormMessages;
  isViewing?: boolean;
};

function SubjectFormPage({ messages, isViewing = false }: Props) {
  const { subjectId } = useParams();
  const isEditing = !!subjectId && !isViewing;
  const isLoadingExisting = !!subjectId;
  const _id = subjectId ? Number(subjectId) : 0;
  const { data, isLoading: isLoadingSubject, error } = useGetSubjectById({ _id });
  const { create, isLoading: isLoadingCreate } = useCreateSubject();
  const { update, isLoading: isLoadingUpdate } = useUpdateSubject();
  const { mutate } = useMutate();
  const router = useNavigate();
  const { lang } = useLanguage();

  const onSuccess = () => {
    mutate(SWR_KEYS.getSubjectById);
    mutate(SWR_KEYS.getSubjects);
    router(ROUTE_URLS.subjects(lang));
  };

  const handleUpdate = async (_formData: SubjectFormData) => {
    if (!data) return;

    const id = Number(data.id);
    const formData = normalizeSubjectFormData(_formData);
    const successMessage = messages.updateSubjectSuccess;
    const errorMessage = messages.updateSubjectError;

    await update({ _id: id, formData, successMessage, errorMessage, onSuccess });
  };

  const handleCreate = async (_formData: SubjectFormData) => {
    const formData = normalizeSubjectFormData(_formData);
    const successMessage = messages.createSubjectSuccess;
    const errorMessage = messages.createSubjectError;

    await create({ formData, successMessage, errorMessage, onSuccess });
  };

  const handleSubmitForm = async (_formData: SubjectFormData) => {
    if (isViewing) return;
    if (isEditing) await handleUpdate(_formData);
    else await handleCreate(_formData);
  };

  const breadCrumb = { title: messages.breadCrumbTitle, href: ROUTE_URLS.subjects(lang) };
  const BREAD_CRUMBS: BreadCrumb[] = [breadCrumb];

  const isSuspended = isLoadingExisting ? isLoadingSubject : false;
  const isLoading = isLoadingCreate || isLoadingUpdate || isLoadingSubject;
  const pageTitle = isViewing
    ? messages.viewPageTitle
    : isEditing
      ? messages.updatePageTitle
      : messages.createPageTitle;

  const shouldLoadForm = isLoadingExisting ? !!data && !error : true;
  const shouldLoadError = !data && !!error && !isLoading && isLoadingExisting;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isSuspended}>
          {shouldLoadForm && (
            <Formik
              initialValues={buildFormInitialData(data)}
              validationSchema={buildSubjectFormSchema(messages)}
              validateOnChange={false}
              enableReinitialize
              onSubmit={handleSubmitForm}
            >
              <SubjectForm messages={messages} isLoading={isLoading} isViewing={isViewing} />
            </Formik>
          )}

          {!!shouldLoadError && (
            <Card sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <AlertBlock
                title={messages.subjectErrorToLoad}
                description={error.message}
                alt={messages.subjectNotFoundAlt}
              />
            </Card>
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default SubjectFormPage;
